import React, { useState, useRef, useEffect, useLayoutEffect, lazy, Suspense, useMemo } from 'react';
import {
    Stepper, Step, StepLabel, Box, Typography, useTheme
} from '@mui/material';
import { StepsContainer, TwoButton, SaveButton, InnerTwoButtons, FooterPart, HeaderPart, StepText } from './ClincSteps/Styles';
import ButtonComponent from 'src/components/shared/Button';
import { ReactComponent as Add } from '../../../../assets/images/add-square.svg';
import { ReactComponent as Previous } from '../../../../assets/images/arrow-left 2.svg';
import { useDispatch } from 'react-redux';
import ClinicInfoStep from './ClincSteps/ClinicInfoStep';
import ClinicLinkStep from './ClincSteps/ClinicLinkStep';
import ClinicSettingStep from './ClincSteps/ClinicSettingStep';
import ClinicTimingStep from './ClincSteps/ClinicTimingStep';
import CmsPagesStep from './ClincSteps/CMSPageStep';
import FacilitiesStep from './ClincSteps/FacilitiesStep';
import MessageTemplateStep from './ClincSteps/MessageTemplateStep';
import PrescriptionTemplateStep from './ClincSteps/PrescriptionTemplateStep';
import ServicesStep from './ClincSteps/ServicesStep';
import TaskSettingStep from './ClincSteps/TaskSettingStep';
import CustomPatientFieldsStep from './ClincSteps/CustomPatientFieldsStep';
import ThankYou from './ClincSteps/ThankYou';
import useResponsive from 'src/components/hooks/useResponsive';
import { getClinicProfileByIdRequest, postClinicProfileRequest } from '../../store/publicActions';
import { getClinicRequest } from '../../store/publicActions';
import storage from 'src/utils/storageUtils';

const ClinicForms = () => {

    const dispatch = useDispatch();
    const { isMobile, isTablet } = useResponsive();
    const Width = isMobile || isTablet ? '18px' : '24px';
    const Height = isMobile || isTablet ? '18px' : '24px';
    const [activeStep, setActiveStep] = useState(0);
    const activeSteps = activeStep > 0;
    const [formData, setFormData] = useState({});
    const [isOtpVerified, setIsOtpVerified] = useState(false);
    const [doctorProfile, setDoctorProfile] = useState(null);
    const [doctorProfileData, setDoctorProfileData] = useState([]);
    const [submitDone, setSubmitDone] = useState(false);
    const [clinicProfile, setClinicProfile] = useState(null);
    const [selectedClinicId, setSelectedClinicId] = useState(null);
    const [clinicProfileData, setClinicProfileData] = useState({});
    const [clinicIds, setClinicId] = useState('');
    const [clinicDetails, setClinicDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const clinicInfoRef = useRef();
    const clinicTimingRef = useRef();
    const prescriptionTempRef = useRef();
    const clinicLinkRef = useRef();
    const clinicSettingRef = useRef();
    const cmsPageRef = useRef();
    const serviceRef = useRef();
    const taskSettingRef = useRef();
    const facilitiesRef = useRef();
    const messageTempRef = useRef();
    const customPatientFieldsRef = useRef();

    const handleFinalSubmit = async (lastStepData) => {
        // Merge the latest step data with existing profile data
        const updatedProfileData = {
            ...clinicProfile,
            ...clinicProfileData,
            clinicSetting: {
                ...(clinicProfile?.clinicSetting || {}),
                ...(clinicProfileData?.clinicSetting || {}),
                ...lastStepData,
                ...(clinicProfileData?.formattedCustomPatientFields ? { customPatientFields: clinicProfileData.formattedCustomPatientFields } : {})
            },
            id: clinicId,
        };

        // Remove customPatientFields if it exists in the root (it should be handled separately or within the model)
        delete updatedProfileData.customPatientFields;
        delete updatedProfileData.formattedCustomPatientFields;

        console.log("Submitting all data finally", updatedProfileData);

        try {
            const response = await new Promise((resolve, reject) => {
                dispatch(postClinicProfileRequest(updatedProfileData, resolve, reject));
            });

            if (response) {
                dispatch(getClinicProfileByIdRequest(clinicId, (res) => {
                    setClinicProfile(res);
                }));
                setSubmitDone(true); // Now we set submitDone to true to show the Thank You page
            }
        } catch (error) {
            console.error("Error submitting final step:", error);
            setSubmitDone(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            const token = storage.get('TOKEN');
            const response = await new Promise((resolve, reject) => {
                dispatch(getClinicRequest(token, resolve, reject));
            });

            let clinicList = [];
            if (Array.isArray(response?.data)) {
                clinicList = response.data;
            } else if (Array.isArray(response)) {
                clinicList = response;
            } else if (Array.isArray(response?.data?.data)) {
                clinicList = response.data.data;
            } else if (response && typeof response === 'object') {
                const possibleArray = Object.values(response).find(val => Array.isArray(val));
                if (possibleArray) clinicList = possibleArray;
            }
            
            console.warn("LATEST BUILD: extracted clinicList", clinicList, "from response", response);
            
            if (clinicList.length > 0) {
                const fetchedClinicId = clinicList[0].id;
                setClinicId(fetchedClinicId);
                
                // Fetch full clinic profile data to populate the forms
                dispatch(getClinicProfileByIdRequest(fetchedClinicId, (res) => {
                    setClinicProfile(res);
                    setClinicProfileData(res); // Also set the active form data
                }));
            }

            setIsLoading(false);
        };

        fetchData();
    }, [dispatch]);

    const clinicId = clinicIds;

    const clinicInfoData = clinicProfileData?.clinicInfo || clinicProfile?.clinicInfo || clinicProfile || {};
    const mergedClinicInfoData = {
        ...clinicInfoData,
        title: clinicInfoData.title || clinicDetails?.clinicName || '',
        address: clinicInfoData.address || clinicDetails?.address || '',
    };

    const stepperData = useMemo(() => [
        { label: 'Clinic Info', Component: <ClinicInfoStep data={mergedClinicInfoData} innerRef={clinicInfoRef} setClinicProfileData={setClinicProfileData} /> },
        { label: 'Clinic Timing', Component: <ClinicTimingStep data={clinicProfileData?.clinicTiming || clinicProfile?.clinicTiming} innerRef={clinicTimingRef} setClinicProfileData={setClinicProfileData} /> },
        { label: 'Prescription Template', Component: <PrescriptionTemplateStep data={clinicProfileData?.prescriptionTemplate || clinicProfile?.prescriptionTemplate} innerRef={prescriptionTempRef} setClinicProfileData={setClinicProfileData} clinicId={clinicId} /> },
        { label: 'Message Template', Component: <MessageTemplateStep data={clinicProfileData?.messageTemplate || clinicProfile?.messageTemplate} innerRef={messageTempRef} setClinicProfileData={setClinicProfileData} clinicId={clinicId} /> },
        { label: 'Clinic Link', Component: <ClinicLinkStep data={clinicProfileData?.clinicLink || clinicProfile?.clinicLink} innerRef={clinicLinkRef} setClinicProfileData={setClinicProfileData} /> },
        { label: 'Services', Component: <ServicesStep data={clinicProfileData?.services || clinicProfile?.services} innerRef={serviceRef} setClinicProfileData={setClinicProfileData} /> },
        { label: 'Facilities', Component: <FacilitiesStep data={clinicProfileData?.facilities || clinicProfile?.facilities} innerRef={facilitiesRef} setClinicProfileData={setClinicProfileData} /> },
        { label: 'Custom Patient Fields', Component: <CustomPatientFieldsStep data={clinicProfileData?.customPatientFields || clinicProfile?.customPatientFields} innerRef={customPatientFieldsRef} setClinicProfileData={setClinicProfileData} clinicId={clinicId} /> },
        {
            label: 'Cms Page',
            Component: (
                <CmsPagesStep
                    data={
                        Array.isArray(clinicProfileData?.cmsPage)
                            ? clinicProfileData.cmsPage
                            : Array.isArray(clinicProfile?.cmsPage)
                                ? clinicProfile.cmsPage
                                : []
                    }
                    innerRef={cmsPageRef}
                    setClinicProfileData={setClinicProfileData}

                />
            )
        },
        { label: 'Task Setting', Component: <TaskSettingStep data={clinicProfileData?.taskSetting || clinicProfile?.taskSetting} innerRef={taskSettingRef} setClinicProfileData={setClinicProfileData} /> },
        { label: 'Clinic Setting', Component: <ClinicSettingStep data={clinicProfileData?.clinicSetting || clinicProfile?.clinicSetting} innerRef={clinicSettingRef} setClinicProfileData={setClinicProfileData} onStepSubmitSuccess={(lastStepData) => handleFinalSubmit(lastStepData)} clinicId={clinicId} /> },

    ], [mergedClinicInfoData, clinicProfileData, clinicProfile, clinicId]);

    const formikRefs = [clinicInfoRef, clinicTimingRef, prescriptionTempRef, messageTempRef, clinicLinkRef, serviceRef, facilitiesRef, customPatientFieldsRef, cmsPageRef, taskSettingRef, clinicSettingRef];

    const stepperRef = useRef(null);

    const handleNext = async () => {
        const currentRef = formikRefs[activeStep];

        if (currentRef?.current?.submitForm) {
            await currentRef.current.submitForm();
        }


        if (currentRef?.current?.isValid === false) return;

        const isLastStep = activeStep === stepperData.length - 1;

        if (!isLastStep) {
            setActiveStep((prev) => prev + 1);
        }
    };


    const handleBack = () => setActiveStep((prev) => prev - 1);
    const handleSkip = () => setActiveStep((prev) => prev + 1);



    useEffect(() => {
        if (stepperRef.current) {
            const stepper = stepperRef.current;
            const steps = stepper.querySelectorAll('.MuiStep-root');
            const activeStepElement = steps[activeStep];
            if (activeStepElement) {
                // Disabled to prevent Chromium 'Page Unresponsive' infinite layout bug
                // activeStepElement.scrollIntoView({
                //     behavior: 'smooth',
                //     inline: 'center',
                //     block: 'nearest',
                // });
            }
        }
    }, [activeStep]);



    return (
        <Box >

            {submitDone && activeStep === stepperData.length - 1 ? (
                <ThankYou />
            )
                :

                (
                    <>
                        <StepsContainer>


                            <HeaderPart>



                                <Typography variant="subtitle2" textAlign="center" mb={2}>
                                    Step {activeStep + 1} of {stepperData.length} — {stepperData[activeStep]?.label}

                                </Typography>

                                <Box
                                    ref={stepperRef}
                                    sx={{
                                        overflowX: 'auto',
                                        whiteSpace: 'nowrap',
                                        scrollbarWidth: 'none',
                                        '&::-webkit-scrollbar': { display: 'none' },

                                    }}
                                >
                                    <Stepper activeStep={activeStep} alternativeLabel sx={{
                                        width: 'max-content',
                                        minWidth: 800,
                                        paddingBottom: 1,
                                    }}>
                                        {stepperData.map(({ label }) => (
                                            <Step key={label}>
                                                <StepLabel
                                                >{label}</StepLabel>
                                            </Step>
                                        ))}
                                    </Stepper>
                                </Box>
                            </HeaderPart>
                            <Box padding="20px">
                                <Box>
                                    {stepperData[activeStep]?.Component}

                                </Box>

                            </Box>
                            <FooterPart sx={{ justifyContent: activeSteps ? 'space-between' : 'flex-end' }}>

                                {activeStep > 0 && (
                                    <ButtonComponent
                                        variant="none"
                                        startIcon={<Previous width={Width} height={Height} />}
                                        onClick={handleBack}>Back</ButtonComponent>
                                )}

                                <InnerTwoButtons>
                                    {/* {activeStep < 8 && (<ButtonComponent variant="none" onClick={handleSkip}>Skip</ButtonComponent>)} */}


                                    <SaveButton
                                        onClick={handleNext}
                                        startIcon={<Add width={Width} height={Height} />}
                                        type="submit">  {activeStep === stepperData.length - 1 ? 'Submit' : 'Next'}</SaveButton>
                                </InnerTwoButtons>

                            </FooterPart>
                        </StepsContainer>
                    </>
                )}
        </Box>
    );
};

export default ClinicForms;
